import React, { useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { motion } from 'framer-motion';

// Define constants for the states
export const LoadingStates = {
  ERROR: 'Error',
  LOADING: 'Loading',
  SUCCESS: 'Success',
};

type LoadingProps = {
  message?: string;
  state?: typeof LoadingStates[keyof typeof LoadingStates];
  overlay?: boolean;
  subtitle?: string;
  image?: string; // Optional image to display with animation during loading
};

const Loading: React.FC<LoadingProps> = ({
  message = "Loading...",
  state = LoadingStates.LOADING,
  overlay = false,
  subtitle,
  image
}) => {
  // Prevent scrolling when overlay is active
  useEffect(() => {
    if (overlay && state === LoadingStates.LOADING) {
      // Prevent scrolling by setting overflow hidden on body
      document.body.style.overflow = 'hidden';

      // Cleanup: restore scrolling when component unmounts
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [overlay, state]);

  const content = (
    <div className={`d-flex flex-column align-items-center justify-content-center ${overlay ? 'text-dark' : ''}`}>
      {state === LoadingStates.LOADING && (
        <>
          {/* Display image with animation if provided */}
          {image && overlay ? (
            <motion.div
              style={{
                position: 'relative',
                maxWidth: '500px',
                maxHeight: '500px',
                marginBottom: '30px',
              }}
            >
              {/* Animated border glow */}
              <motion.div
                animate={{
                  rotate: 360,
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: 'linear',
                }}
                style={{
                  position: 'absolute',
                  inset: '-4px',
                  borderRadius: '16px',
                  background: 'linear-gradient(45deg, #3b82f6, #8b5cf6, #ec4899, #3b82f6)',
                  backgroundSize: '300% 300%',
                  filter: 'blur(8px)',
                  zIndex: 0,
                }}
              />

              {/* Image container */}
              <div
                style={{
                  position: 'relative',
                  zIndex: 1,
                  borderRadius: '12px',
                  overflow: 'hidden',
                  backgroundColor: '#000',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
                }}
              >
                <img
                  src={image}
                  alt="Processing"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              </div>
            </motion.div>
          ) : (
            /* Default spinner if no image provided */
            <Spinner
              animation="border"
              variant={overlay ? "primary" : "secondary"}
              role="status"
              style={overlay ? { width: '4rem', height: '4rem' } : {}}
              className={overlay ? "mb-4" : ""}
            />
          )}

          {/* Loading Messages */}
          {overlay ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              style={{
                textAlign: 'center',
                color: image ? 'white' : 'inherit',
                position: 'relative',
                zIndex: 10
              }}
            >
              {!image && (
                <motion.div
                  animate={{ opacity: [1, 0.6, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <div className={`spinner-border mb-3 ${image ? 'text-light' : 'text-primary'}`} role="status" style={{ display: 'none' }}>
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </motion.div>
              )}
              <h3 className="mb-2 text-center">{message}</h3>
              {subtitle && (
                <p className={`mb-0 text-center ${image ? 'text-light opacity-75' : 'text-muted'}`}>
                  {subtitle}
                </p>
              )}
            </motion.div>
          ) : (
            <small className="text-secondary mt-1">{message}</small>
          )}
        </>
      )}
      {state === LoadingStates.ERROR && (
        <>
          <div className="alert alert-danger" role="alert">
            <strong>Error:</strong> {message}
          </div>
        </>
      )}
      {state === LoadingStates.SUCCESS && (
        <>
          <div className="alert alert-success" role="alert">
            <strong>Success:</strong> {message}
          </div>
        </>
      )}
    </div>
  );

  if (overlay && state === LoadingStates.LOADING) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: image ? 'rgba(0, 0, 0, 0.85)' : '#ffffff', // Dark when showing image, white otherwise
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}
      >
        {content}
      </div>
    );
  }

  return content;
};

export default Loading;